import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Clock, Star, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

type CourseCardProps = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  level: string | null;
  price: number | null;
  currency: string | null;
  duration_hours: number | null;
  is_featured: boolean | null;
  instructor?: {
    full_name: string;
    avatar_url: string | null;
  };
  enrollment_count?: number;
  average_rating?: number;
  review_count?: number;
};

export const CourseCard = ({
  slug,
  title,
  description,
  thumbnail_url,
  level,
  price,
  currency,
  duration_hours,
  is_featured,
  instructor,
  enrollment_count = 0,
  average_rating = 0,
}: CourseCardProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const getLevelColor = (level: string | null) => {
    switch (level?.toLowerCase()) {
      case "beginner":
        return "bg-holographic-green/20 text-holographic-green border-holographic-green/30";
      case "intermediate":
        return "bg-holographic-cyan/20 text-holographic-cyan border-holographic-cyan/30";
      case "advanced":
        return "bg-holographic-pink/20 text-holographic-pink border-holographic-pink/30";
      default:
        return "bg-muted/20 text-muted-foreground border-muted/30";
    }
  };

  return (
    <div 
      className="glass-card rounded-lg overflow-hidden hover-slide-up group cursor-pointer"
      onClick={() => navigate(`/courses/${slug}`)}
    >
      {/* Thumbnail */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-holographic-cyan/10 via-holographic-pink/10 to-holographic-blue/10">
        {thumbnail_url ? (
          <img
            src={thumbnail_url}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-6xl font-bold text-holographic opacity-30">
              {title.charAt(0)}
            </div>
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {is_featured && (
            <Badge className="bg-holographic-pink/90 text-white border-0">
              {t("courses.featured")}
            </Badge>
          )}
          {level && (
            <Badge className={getLevelColor(level)}>
              {t(`courses.${level.toLowerCase()}`)}
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Title & Description */}
        <div>
          <h3 className="text-xl font-bold text-foreground mb-2 line-clamp-2 group-hover:text-holographic transition-colors">
            {title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {description}
          </p>
        </div>

        {/* Instructor */}
        {instructor && (
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarImage src={instructor.avatar_url || ""} />
              <AvatarFallback className="text-xs">
                {instructor.full_name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground">{t("courses.instructor")}</p>
              <p className="text-sm font-medium text-foreground truncate">
                {instructor.full_name}
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {duration_hours && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{duration_hours} {t("courses.hours")}</span>
            </div>
          )}
          {average_rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-holographic-cyan text-holographic-cyan" />
              <span>{average_rating.toFixed(1)}</span>
            </div>
          )}
          {enrollment_count > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{enrollment_count} {t("courses.students")}</span>
            </div>
          )}
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div>
            {price && price > 0 ? (
              <p className="text-2xl font-bold text-holographic">
                {currency || "USD"} {price}
              </p>
            ) : (
              <p className="text-2xl font-bold text-holographic-green">
                {t("courses.free")}
              </p>
            )}
          </div>
          <Button variant="outline" className="group-hover:bg-holographic-cyan/10 group-hover:border-holographic-cyan">
            {t("courses.viewCourse")}
          </Button>
        </div>
      </div>
    </div>
  );
};
